import { InteractionEvent } from '@tencent/alloy-renderer';
import {
    isContextMenuEvent, isMetaOrShiftOrCtrlKey, isShiftKey,
} from '@src/utils/key-mouse-event/mouse-event-check';
import { ITableModel } from '@src/slide/domain/ui-model/element-modeles/Interface/table-model.interface';
import { InterfaceFactoryOptions } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/factory-options.interface';
import type { ComponentFactory } from '@src/slide/content-viewer/service/canvas-renderer/core/canvas-render/elements/component-factory';
import {
    CommonEditLogicWithTextEditor,
} from '@src/slide/content-viewer/service/canvas-renderer/entry/pc-edit/logic/common-edit-logic-with-text-editor';
import {
    addKeyMouseEvents,
    removeMouseEvents,
} from '@utils/key-mouse-event';
import { IReactionDisposer, reaction, transaction } from 'mobx';
import { RowColResizeComponent } from './table-edit-layer/row-col-resize-component';
import { CellSelectComponent } from './table-edit-layer/cell-select-component';
import { TableSelectonComponent } from './table-edit-layer/table-selection-component';
import ObserverFactory from '@src/slide/application/service/modelObserver/observerFactory';
import { RegionType } from '@src/slide/domain/selection/constant/region';
import { PPTEditorStateType } from '@src/slide/domain/ui-model/constant/edit-state-type';
import { IPcEditorAppServices } from '@src/slide/application/entry/toc/editor/pc/pc-editor-app-services.interface';
import { UIModelType } from '@src/slide/domain/ui-model/element-modeles/constant';

interface ICoordinate {
    row: number;
    col: number;
}

export class TableEditLogic extends CommonEditLogicWithTextEditor {
    declare protected readonly factoryOptions: InterfaceFactoryOptions<ITableModel>;

    private beginCoord: ICoordinate | null = null;
    private lastCoord: ICoordinate | null = null;
    private shiftKeyBeginCoord: ICoordinate | null = null;

    private config: Record<string, (e: InteractionEvent) => void>;

    private cellSelectComponent: CellSelectComponent | null = null;
    private rowColResizeComponent: RowColResizeComponent | null = null;
    private tableSelectonComponent: TableSelectonComponent | null;

    private selectedReaction: IReactionDisposer;
    private selectedRangeReaction: IReactionDisposer;
    private selectElementChangeReaction: IReactionDisposer;

    public constructor(factoryOptions: InterfaceFactoryOptions<ITableModel>, component: ComponentFactory) {
        super(factoryOptions, component);

        this.cellSelectComponent = new CellSelectComponent(factoryOptions);
        this.component.inner.addChild(this.cellSelectComponent);
        this.cellSelectComponent.updateComponent();

        this.rowColResizeComponent = new RowColResizeComponent(factoryOptions);
        this.component.inner.addChild(this.rowColResizeComponent);
        this.rowColResizeComponent.updateComponent();

        this.tableSelectonComponent = new TableSelectonComponent(factoryOptions);
        this.component.inner.addChild(this.tableSelectonComponent);

        this.config = {
            enter: this.handleTableEnterKeyDown,
            tab: this.handleTableTabKeyDown,
        };

        component.inner?.onMouseDown(this.handleTableMouseDown);
        component.inner?.onMouseMove(this.handleTableMouseMove);
        window.addEventListener('mouseup', this.handleTableMouseUp);

        const { model } = this.factoryOptions;
        this.selectedReaction = reaction(() => ({
            selected: model.selected,
        }), (items) => {
            if (items.selected) {
                this.enableShortCutKey();
            } else {
                this.disableShortCutKey();
                this.shiftKeyBeginCoord = null;
            }
            this.rowColResizeComponent?.updateComponent();
        });

        this.selectedRangeReaction = reaction(() => ({
            selectedRange: model.selectedRange,
        }), () => {
            this.tableSelectonComponent?.updateComponent();
        });

        this.selectElementChangeReaction = reaction(() => ({
            selectedArray: this.getSelectionService().selectedElements.selectedArray,
        }), ({ selectedArray }) => {
            this.handleSelectElementChange(selectedArray);
        });

        const observer = ObserverFactory.createObserver(model);
        observer?.addChangesetObserver(this.handleChangesetObserver);
    }

    public dispose(): void {
        super.dispose();

        this.selectedReaction();
        this.selectedRangeReaction();
        this.selectElementChangeReaction?.();
        this.disableShortCutKey();
        const observer = ObserverFactory.createObserver(this.factoryOptions.model);
        observer.removeChangesetObserver(this.handleChangesetObserver);

        this.cellSelectComponent?.dispose();
        this.rowColResizeComponent?.dispose();
        this.tableSelectonComponent?.dispose();

        this.cellSelectComponent = null;
        this.rowColResizeComponent = null;
        this.tableSelectonComponent = null;

        window.removeEventListener('mouseup', this.handleTableMouseUp);
    }

    public enableShortCutKey = (): void => {
        // @ts-ignore
        addKeyMouseEvents(this.config);
    };

    public disableShortCutKey = (): void => {
        removeMouseEvents(this.config);
    };

    public makeElementSelected = (e?: MouseEvent) => {
        (this.getAppService() as IPcEditorAppServices).editableServiceLayer?.selectedAnims.clear();
        this.getSelectionService().lastMouseDownRegion = RegionType.EDITOR;
        this.getSelectionService().selectedSlides.clear();

        if (this.getComponent().selectable) {
            if (e && isMetaOrShiftOrCtrlKey(e) && !isContextMenuEvent(e)) {
                if (!this.getComponent().selected) {
                    this.getSelectionService().selectedElements.add(this.getComponent());
                } else if (this.getSelectionService().selectedElements.length > 1) {
                    if (isShiftKey(e)) {
                        return;
                    }

                    this.getSelectionService().selectedElements.remove(this.getComponent());
                }
            } else if (!this.getComponent().selected || this.getSelectionService().selectedElements.length > 1) {
                transaction(() => {
                    this.getSelectionService().selectedElements.clear();
                    this.getSelectionService().selectedElements.add(this.getComponent());
                });
            }
        }
    };

    public getIndex(array: Array<number>, offset: number): number {
        let length = 0;
        const count = array.length;
        for (let index = 0; index < count; index++) {
            length += array[index];
            if (length >= offset) {
                return index;
            }
        }
        return count - 1;
    }

    public renderSearchHighlight({ searchResult, highlightResult, keyword }) {
        // TODO 这里用了一个searchReplaceManage上的方法，比较好的方式是把这个抽出来。后续这里需要优化
        const searchReplaceManager = (this.getAppService() as IPcEditorAppServices)
            .editableServiceLayer?.searchReplaceManager?.dangerousInstance;
        const fixedResult = searchReplaceManager?.fixTableResult(searchResult);
        fixedResult?.forEach(({ cell, textPositions }) => {
            const textEditor = this.factoryOptions.model.tableItems[cell.row][cell.col]?.editorInstance;
            textEditor?.addSearchMark(textPositions, keyword);
        });

        if (highlightResult?.cell) {
            const { cell } = highlightResult;
            const textEditor = this.factoryOptions.model.tableItems[cell.row][cell.col]?.editorInstance;
            textEditor?.addHighlight([highlightResult], keyword);
        }
    }

    // todo: 表格接入interface，现在继承的是类而不是接口
    public handleSelectionChange() {}

    private handleChangesetObserver = () => {
        this.cellSelectComponent?.updateComponent();
        this.rowColResizeComponent?.updateComponent();
        this.tableSelectonComponent?.updateComponent();
    };

    private getCellCoordinate(e: InteractionEvent): ICoordinate {
        const nativeEvent = e.nativeEvent as MouseEvent;
        const scale = this.factoryOptions.getPageScale();

        const canvasPosition = this.getWorldPosition();
        const { minX, minY } = this.component.inner.children[1].worldBounds;
        const offsetX = (nativeEvent.clientX - canvasPosition.x - minX) / scale;
        const offsetY = (nativeEvent.clientY - canvasPosition.y - minY) / scale;

        const { colWidths } = this.factoryOptions.model.props;
        const { displayRowHeights } = this.factoryOptions.model;
        const row = this.getIndex(displayRowHeights, offsetY);
        const col = this.getIndex(colWidths, offsetX);
        return { row, col };
    }

    private handleTableEnterKeyDown = (e: InteractionEvent): void => {
        if (this.factoryOptions.model.showSelection) {
            this.factoryOptions.model.enterFirstSelectedCell();
        }
    };

    private handleTableTabKeyDown = (e: InteractionEvent): void => {
        this.factoryOptions.model.selectNextCell();
    };

    private checkCurrentEditable(): boolean {
        const { model } = this.factoryOptions;
        return (model.currentEditState as number) > PPTEditorStateType.EDITABLE;
    }

    private rightClickInSelectedArea(e: InteractionEvent, row, col): boolean {
        const { model } = this.factoryOptions;
        return (isContextMenuEvent(e.nativeEvent as MouseEvent) && model.showSelection
            && model.cellSelectedState(row, col, model.selectedRange));
    }

    // eslint-disable-next-line complexity
    private handleTableMouseDown = (e: InteractionEvent): void => {
        const { model } = this.factoryOptions;
        if (this.checkCurrentEditable()) {
            this.getAppService().viewableServiceLayer?.tipsManager?.showCannotEditTips();
            return;
        }

        this.makeElementSelected(e.nativeEvent as MouseEvent);
        e.stopPropagation();

        const beginCoord = this.getCellCoordinate(e);
        // 选中区右键不触发选区变更，在此将事件阻止冒泡
        if (this.rightClickInSelectedArea(e, beginCoord.row, beginCoord.col)) {
            return;
        }

        const coord = this.getCellCoordinate(e);
        if (isShiftKey(e.nativeEvent as MouseEvent)) {
            const { model } = this.factoryOptions;
            model.showSelection = true;
            this.tableSelectCellRange(
                this.shiftKeyBeginCoord?.row ?? 0,
                this.shiftKeyBeginCoord?.col ?? 0,
                coord.row,
                coord.col,
            );
            return;
        }
        // 按住shift键来多远单元格的起始单元格 -> 没按住shift时的单击的单元格
        // 期间按住shift无论怎么点击，起始单元格都不变。 只有再次不按住shift点击才会更新
        // 表格失焦时，此起始记录才作废
        this.shiftKeyBeginCoord = coord;


        this.beginCoord = beginCoord;
        this.lastCoord = beginCoord;
        const { selectedRange } = model;
        if (model.showSelection || !selectedRange
            || !(this.beginCoord.row === selectedRange.rowStart && this.beginCoord.row === selectedRange.rowEnd
                && this.beginCoord.col === selectedRange.colStart && this.beginCoord.col === selectedRange.colEnd)) {
            model.showSelection = false;
            this.tableSelectCellRange(
                this.beginCoord.row,
                this.beginCoord.col,
                this.lastCoord.row,
                this.lastCoord.col,
            );
        }
    };

    private handleTableMouseMove = (e: InteractionEvent): void => {
        if (!this.beginCoord) return;
        const currentCoord = this.getCellCoordinate(e);
        if (currentCoord.row !== this.lastCoord?.row || currentCoord.col !== this.lastCoord?.col) {
            this.lastCoord = currentCoord;
            const mergedCellRange = this.factoryOptions.model.getMergedCellRangeForCoords(
                this.beginCoord.row,
                this.beginCoord.col,
            );
            const mergedCell = this.factoryOptions.model.tableItems[mergedCellRange.rowStart][mergedCellRange.colStart];
            this.factoryOptions.model.showSelection = !(
                currentCoord.row >= mergedCell.row && currentCoord.row < mergedCell.row + mergedCell.rowspan
                && currentCoord.col >= mergedCell.col && currentCoord.col < mergedCell.col + mergedCell.colspan);
            this.tableSelectCellRange(
                this.beginCoord?.row,
                this.beginCoord?.col,
                this.lastCoord?.row,
                this.lastCoord?.col,
            );
        }
    };

    private handleTableMouseUp = (): void => {
        this.beginCoord = null;
        this.lastCoord = null;
    };

    private getComponent(): ITableModel {
        return this.factoryOptions.model;
    }

    private getWorldPosition = () => {
        const canvas = this.factoryOptions?.getApplication?.().view?.native as HTMLCanvasElement;
        if (!canvas) {
            return { x: 0, y: 0 };
        }
        const { left, top } = canvas?.getBoundingClientRect?.() ?? { left: 0, top: 0 };
        return {
            x: left, y: top,
        };
    };

    private handleSelectElementChange(selectedArray) {
        const { model } = this.factoryOptions;
        if (model) {
            if (selectedArray?.indexOf(model) >= 0) {
                if (selectedArray.length > 1) {
                    model.uploadEditingCellData(true);
                }
                this.tableSelectCellRange(
                    0, 0,
                    model.props.rowHeights.length - 1,
                    model.props.colWidths.length - 1,
                );
            } else {
                // 失去焦点
                model.uploadEditingCellData(true);
                model.showSelection = false;
                model.selectedCellRanges = [];
                model.selectedRange = null;
            }
        }
    }

    private tableSelectCellRange(rowStart: number, colStart: number, rowEnd: number, colEnd: number) {
        const { model } = this.factoryOptions;
        const selectedArray = this.getSelectionService().selectedElements.selectedArray || [];
        // 只有当前表格处于编辑态，且只有当前表格选中时，才能复选单元格
        if (selectedArray.length === 1 && selectedArray[0] === model) {
            model.afterSelection(rowStart, colStart, rowEnd, colEnd);
        } else if (selectedArray.length > 1) {
            selectedArray
                .filter(model => model.type === UIModelType.TABLE)
                .forEach((model: ITableModel) => {
                    model.showSelection = false;
                    model.afterSelection(
                        0, 0,
                        model.props.rowHeights.length - 1,
                        model.props.colWidths.length - 1,
                    );
                });
        }
    }
}
